import React, { Component } from 'react';
import cloneDeep from 'lodash/cloneDeep';
import isNil from 'lodash/isNil';
import kebabCase from 'lodash/kebabCase';
import mergeWith from 'lodash/mergeWith';
import uniqBy from 'lodash/uniqBy';
import moment from 'moment';
import 'moment-timezone';
import { compose, graphql } from 'react-apollo';
import { Modal } from 'react-bootstrap';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';
import { Link, withRouter } from 'react-router-dom';
import { SUBSCRIPTION_PREFERRED_LOCATION_CHANGED } from 'containers/DesktopNotificationTray';
import A11ySvg from 'components/A11ySvg';
import ADialog from 'components/ADialog';
import AlphaVideo from 'components/AlphaVideo';
import CloseButton from 'components/CloseButton';
import GameChat from 'components/GameChat';
import IconButton from 'components/IconButton';
import Input from 'components/Input';
import LoadingIndicator from 'components/LoadingIndicator';
import NotificationWindow from 'components/NotificationWindow';
import ProfileIcon from 'components/ProfileIcon';
import StepperInput from 'components/StepperInput';
import TextButton from 'components/TextButton';
import TextCycler from 'components/TextCycler';
import TimeSyncWrapper from 'utils/TimeSync/TimeSyncWrapper';
import { LG_BREAKPOINT_AND_UP } from 'utils/constants';
import { formatNumber } from 'utils/formatting';
import { getUserData } from 'utils/graphQLFunctions';
import withMediaQuery from 'utils/withMediaQuery';
import ChatIcon from 'resources/Footer-Icon-Chat.png';
import CloseIcon from 'resources/cancel-circle.svg';
import LeaderboardIcon from 'resources/Footer-Icon-Leaderboard.png';
import WScoreIcon from 'resources/ui-icon-wscore.png';
import { getDateTime } from '../common';
import Cycler from '../components/Cycler';
import Prize from '../components/Prize';
import {
  MUTATION_ADD_BID,
  QUERY_GET_AUCTION,
  QUERY_GET_WSCORE,
  SUBSCRIPTION_AUCTION_EVENT,
  SUBSCRIPTION_AUCTION_UPDATE,
  SUBSCRIPTION_UPDATED_CURRENCY_ITEM_BALANCES
} from '../graphql';
import { truncate } from '../util';
import AuctionCountdown from './AuctionCountdown';
import {
  InProgressAuctionBidInfo,
  UpcomingAuctionBidInfo
} from './AuctionInfo';
import PrizeIcon from './PrizeIcon';
import AnimationBidClose from './assets/AuctionAnim-BidClose.mp4';
import { ReactComponent as HintIconSvg } from './assets/Auctions_ExclamationPoint.svg';
import './Auction.css';

const READ_TITLE_TEXT_LIMIT = 45;
const READ_TITLE_TEXT_LIMIT_TRIM = READ_TITLE_TEXT_LIMIT - 25;

const READ_DESCRIPTION_LIMIT = 180;
const READ_DESCRIPTION_LIMIT_TRIM = READ_DESCRIPTION_LIMIT - 15;

class AuctionPage extends Component {
  state = {
    myBid: 0,
    editBid: false,
    bidToSet: null,
    bidAttempt: null,
    isErrorModalOpen: false,
    isConfirmBidModalOpen: false,
    isMinBidErrorModalOpen: false,
    isAuctionCloseErrorModalOpen: false,
    isMaxBidErrorModalOpen: false,
    hasAnimBidClosedFinished: false,
    extendedBy: null,
    endTime: null,
    overlayContent: null,
    showLeaderboardModal: false,
    showChatModal: false,
    isModalConfirmationChecked: false
  };

  handleAddBid = async ({ amount }) => {
    return this.setState({
      ...(!this.state.isModalConfirmationChecked && {
        isConfirmBidModalOpen: true
      }),
      bidAttempt: amount
    });
  };

  handleConfirmBid = async ({ amount, min, max, bidIncrement }) => {
    const { createBid, match } = this.props;
    const { id } = match.params;

    if (amount < min) {
      return this.setState({
        isConfirmBidModalOpen: false,
        isMinBidErrorModalOpen: true,
        bidAttempt: amount,
        editBid: false
      });
    }
    if (amount > max) {
      return this.setState({
        isConfirmBidModalOpen: false,
        isMaxBidErrorModalOpen: true,
        bidAttempt: amount,
        editBid: false
      });
    }

    this.setState({ isConfirmBidModalOpen: false });
    try {
      await createBid({
        auctionId: id,
        amount
      });
      this.setState({
        bidAttempt: amount,
        bidToSet: amount + bidIncrement,
        editBid: false
      });
    } catch (error) {
      let errorString = error.toString();
      if (errorString.includes("invalid auction's bid value")) {
        this.setState({ isMinBidErrorModalOpen: true, editBid: false });
      } else if (errorString.includes("invalid auction's value")) {
        this.setState({ isAuctionCloseErrorModalOpen: true, editBid: false });
      } else {
        this.setState({ isErrorModalOpen: true, editBid: false });
      }
    }
  };

  handleConfirmBidModalClose = () => {
    this.setState({
      isConfirmBidModalOpen: false,
      bidAttempt: null,
      editBid: false
    });
  };
  handleSuccessModalClose = () => {
    this.setState({
      bidAttempt: null,
      editBid: false
    });
  };

  toggleOverlay = overlayContent => {
    this.setState({
      overlayContent: overlayContent || null
    });
  };

  handleErrorModalClose = () => {
    this.setState({ isErrorModalOpen: false, bidAttempt: null });
  };

  handleAuctionCloseErrorModalClose = () => {
    this.setState({ isAuctionCloseErrorModalOpen: false, bidAttempt: null });
  };

  handleMinBidErrorModalClose = () => {
    this.setState({ isMinBidErrorModalOpen: false, bidAttempt: null });
  };

  handleMaxBidErrorModalClose = () => {
    this.setState({ isMaxBidErrorModalOpen: false, bidAttempt: null });
  };

  handleAnimBidCloseFinish = () =>
    this.setState({ hasAnimBidClosedFinished: true });

  handleUserPreferredLocationUpdate = () => {
    this.props.history.push('/auctions');
  };

  onSubscribeToEventsUpdate = (extEndTime, auction) => {
    if (extEndTime) {
      if (this.state.endTime) {
        let tempExtendedEndTime = getDateTime(this.state.endTime);
        let tempExtEndTime = getDateTime(extEndTime);
        if (tempExtEndTime.isAfter(tempExtendedEndTime)) {
          this.setState({
            endTime: extEndTime,
            extendedBy: moment.duration(
              tempExtEndTime.diff(tempExtendedEndTime)
            )
          });
        }
      } else {
        this.setState({
          endTime: extEndTime,
          extendedBy: moment.duration(auction.incrementGap, 'seconds')
        });
      }
    }
  };

  subscribe = () => {
    // An ugly way to pass the user id to the subscription,
    // since it has no access to the props in runtime AFAIK
    const { userData } = this.props;
    const userId = userData.user.userId;

    this.unsubscribeFromEvents = this.props.subscribeToEvents(
      userId,
      this.onSubscribeToEventsUpdate
    );
    this.unsubscribeFromUpdates = this.props.subscribeToUpdates();
    this.unsubscribeFromWScoreData = this.props.subscribeToWScoreData();
    this.unsubscribeToLocationData = this.props.subscribeToLocationData();
    this.hasSubscribed = true;
  };

  componentDidUpdate(prev) {
    const { auctionData, userData } = this.props;
    const { auctionData: prevAuctionData, userData: prevUserData } = prev;

    const prevPreferred = prevUserData?.user?.preferredLocation;
    const currentPreferred = userData?.user?.preferredLocation;

    if (prevPreferred !== undefined && currentPreferred !== undefined) {
      if (prevPreferred !== currentPreferred) {
        this.handleUserPreferredLocationUpdate();
      }
    }

    const endTime =
      auctionData && auctionData.data ? auctionData.data.endTime : null;
    const prevEndTime =
      prevAuctionData && prevAuctionData.data
        ? prevAuctionData.data.endTime
        : null;

    if (endTime !== prevEndTime) {
      this.setState({
        endTime: auctionData.data.extendedEndTime || endTime
      });
    }
  }

  componentWillUnmount() {
    this.unsubscribeFromEvents && this.unsubscribeFromEvents();
    this.unsubscribeFromUpdates && this.unsubscribeFromUpdates();
    this.unsubscribeFromWScoreData && this.unsubscribeFromWScoreData();
    this.unsubscribeToLocationData && this.unsubscribeToLocationData();
  }

  sortBidsPosition(bids) {
    const rankedBids = bids
      .filter(
        item =>
          !bids.find(
            i => i.user.userId === item.user.userId && i.amount > item.amount
          )
      )
      .slice(0, 40)
      .map(item => {
        return item;
      });
    return rankedBids;
  }

  //TODO: This could be added to the countdown container at top of the auction? Where can this go? THere is no mockup.
  //   <div className="auction-time-title-extended">
  //   {extendedBy && (
  //     <ExtendedBy duration={extendedBy} key={endTime} />
  //   )}
  // </div>

  getOverlayContent = () => {
    const className = `info-overlay ${
      this.state.overlayContent ? 'in' : 'out'
    }`;
    return (
      <div className={className} aria-hidden="true">
        <div className="close-overlay">
          <TextButton onClick={() => this.toggleOverlay(null)}>
            <img src={CloseIcon} alt="Close overlay" />
          </TextButton>
        </div>
        <div className="overlay-content">{this.state.overlayContent}</div>
      </div>
    );
  };

  renderWinnerDisplayName(winner) {
    const MAX_LENGTH = 49;
    if (winner.length > MAX_LENGTH) {
      const tip = (
        <Tooltip className="auction-tooltip one-auction name" id="tooltip-name">
          {winner}
        </Tooltip>
      );
      const showMore = (
        <OverlayTrigger
          overlay={tip}
          placement="bottom"
          delayShow={100}
          delayHide={200}
        >
          <span>...</span>
        </OverlayTrigger>
      );
      winner = (
        <div className="auction-completed-winner">
          {winner}
          {showMore}
        </div>
      );
    } else {
      winner = <div className="auction-completed-winner">{winner}</div>;
    }
    return <div className="notranslate">{winner}</div>;
  }

  renderLeaderBoard(data, userData, bids, endTime, prizes) {
    const { user: me } = userData;
    const sortedRankedBids = this.sortBidsPosition(bids);
    const yourPlace =
      sortedRankedBids.length && data.lastOwnBid
        ? sortedRankedBids.findIndex(
            item => item.amount === data.lastOwnBid.amount
          ) + 1
        : null;
    const lastOwnBid = data.lastOwnBid ? data.lastOwnBid.amount : '';
    const prizeIcons = uniqBy(prizes, 'category').map(item => (
      <PrizeIcon key={item.category} category={item.category} />
    ));
    const prizeNames = uniqBy(prizes, 'category').map(item => item.name);

    return (
      <div
        className={`auction-leaderboard${
          data.status === 'Upcoming' ? ' auction-leaderboard-upcoming' : ''
        }`}
      >
        <div className="auction-leaderboard-header">
          <div className="auction-leaderboard-header-icon">
            <Cycler items={prizeIcons} />
          </div>
          <div className="auction-leaderboard-header-prize">
            <div className="auction-leaderboard-header-prize-title">
              {prizes.length === 1
                ? prizes.length + ' prize'
                : prizes.length + ' prizes'}
            </div>
            <div className="auction-leaderboard-header-prize-value">
              <TextCycler items={prizeNames} />
            </div>
          </div>
          <div className="auction-leaderboard-header-misc">
            {data.status === 'Upcoming' ? (
              <div className="auction-leaderboard-header-upcoming">
                Not Yet Active
              </div>
            ) : (
              <>
                <div className="auction-leaderboard-header-misc-title">
                  {data.totalBidsCount === 1
                    ? data.totalBidsCount + ' Bidder'
                    : data.totalBidsCount
                    ? data.totalBidsCount + ' Bidders'
                    : '0 Bidders'}
                </div>
                <div className="auction-leaderboard-header-misc-value">
                  {data.status === 'Completed' ? (
                    <span>Auction Completed</span>
                  ) : (
                    <>
                      <span>Ends In: </span>
                      <AuctionCountdown inProgress={true} endTime={endTime} />
                    </>
                  )}
                </div>
              </>
            )}
          </div>
        </div>

        <div className="auction-leaderboard-body">
          {!!bids.length &&
            bids
              .filter(
                item =>
                  !bids.find(
                    i =>
                      i.user.userId === item.user.userId &&
                      i.amount > item.amount
                  )
              )
              .slice(0, 20)
              .map((item, idx) => {
                const place = idx + 1;
                return (
                  <div
                    key={item.amount}
                    className={`auction-leaderboard-row${
                      place === 1 ? ' auction-leaderboard-row-top' : ''
                    }${
                      place === 1 && place === yourPlace
                        ? ' auction-leaderboard-row-gold'
                        : ''
                    }`}
                  >
                    <div className="auction-leaderboard-row-col">
                      <div
                        className="auction-leaderboard-row-avatar"
                        title={item.user.displayName}
                      >
                        <ProfileIcon
                          avatarData={item.user.avatarData}
                          size={40}
                          withBorder
                        />
                      </div>
                      <div
                        className={`auction-leaderboard-row-bid${
                          place === 1
                            ? ' auction-leaderboard-row-bid-extended'
                            : ' auction-leaderboard-row-bid-regular'
                        }`}
                      >
                        <div
                          className="auction-leaderboard-row-bid-title notranslate"
                          title={item.user.displayName}
                        >
                          {item.user.displayName}
                        </div>
                        <div className="auction-leaderboard-row-bid-value notranslate">
                          {formatNumber(item.amount)}
                        </div>
                      </div>
                    </div>
                    <div className="auction-leaderboard-row-col">
                      <div
                        className="auction-leaderboard-row-time"
                        title={getDateTime(item.bidTime).format(
                          'MM/DD/YYYY hh:mm A'
                        )}
                      >
                        {getDateTime(item.bidTime).format('hh:mm:ss A')}
                      </div>
                      <div className="auction-leaderboard-row-place notranslate">
                        {place}
                      </div>
                    </div>
                  </div>
                );
              })}
        </div>

        <div className="auction-leaderboard-footer">
          <div title={me.displayName}>
            <ProfileIcon avatarData={me.avatarData} size={40} withBorder />
          </div>
          <div className={'auction-leaderboard-row-footer'}>
            <div
              className="auction-leaderboard-row-bid-title notranslate"
              title={me.displayName}
            >
              {me.displayName}
            </div>
            <div className="auction-leaderboard-row-bid-value">
              {formatNumber(lastOwnBid)}
            </div>
          </div>
          <div
            className={'pos notranslate' + (yourPlace === 1 ? ' first' : '')}
          >
            {yourPlace ? yourPlace : '-'}
          </div>
        </div>
      </div>
    );
  }

  renderLeaderboardModalButton() {
    const { auctionData, userData } = this.props;
    const { data } = auctionData;
    const endTime = this.state.endTime || data.extendedEndTime || data.endTime;
    const prizes = data.prizes.prizes;
    const bids = data.bids ? [...data.bids] : [];
    bids.sort((a, b) => b.amount - a.amount);

    return (
      <>
        <button
          className="show-leaderboard-button"
          aria-label="Show leaderboard button"
          onClick={() => this.setState({ showLeaderboardModal: true })}
          type="button"
        >
          <img src={LeaderboardIcon} alt="Show leaderboard" />
        </button>
        <Modal
          className="auction-leaderboard-modal"
          show={this.state.showLeaderboardModal}
          onHide={() => this.setState({ showLeaderboardModal: false })}
        >
          <Modal.Body>
            {this.renderLeaderBoard(data, userData, bids, endTime, prizes)}
          </Modal.Body>
        </Modal>
      </>
    );
  }

  renderChatModalButton() {
    const { auctionData } = this.props;
    const { data } = auctionData;

    return (
      <>
        <button
          className="show-chat-button"
          aria-label="Show chat button"
          onClick={() => this.setState({ showChatModal: true })}
          type="button"
        >
          <img src={ChatIcon} alt="Show chat" />
        </button>
        <Modal
          className="chat-modal"
          show={this.state.showChatModal}
          onHide={() => this.setState({ showChatModal: false })}
        >
          <Modal.Body>
            <GameChat
              room={`auc_${data.id}`}
              title="Auction Chat"
              participantData={data.bids}
            />
          </Modal.Body>
        </Modal>
      </>
    );
  }

  renderHeaderCountdown(auctionStatus, endTime, startTime) {
    const { auctionData } = this.props;
    const { data } = auctionData;

    return (
      <>
        {auctionStatus === 'Upcoming' && <span>starts in</span>}
        {auctionStatus === 'Completed' && <span>Closed</span>}
        {auctionStatus === 'Upcoming' && (
          <>
            <strong>
              <AuctionCountdown startTime={startTime} />
            </strong>
            {!!data.renewalCount && (
              <Hint
                thresholdGap={data.thresholdGap}
                incrementGap={data.incrementGap}
                renewalCount={data.renewalCount}
              />
            )}
          </>
        )}
        {auctionStatus === 'InProgress' && (
          <strong>
            <AuctionCountdown inProgress={true} endTime={endTime} />
          </strong>
        )}
      </>
    );
  }

  renderBodyAuctionDescription(data, prizes, auctionDescription) {
    const className =
      data.status === 'Upcoming' ? 'upcoming-auction' : 'active-auction';
    const auctionName = data.name;
    return (
      <>
        <div className={`auction-body-title ${className}`}>
          {truncate(
            auctionName,
            READ_TITLE_TEXT_LIMIT,
            READ_TITLE_TEXT_LIMIT_TRIM
          )}
        </div>
        <div className="auction-body-details-container">
          <div className="auction-body-prize">
            <Prize
              prizes={prizes}
              auctionName={auctionName}
              auctionDescription={auctionDescription}
            />
          </div>
          <div className="auction-body-description">
            {truncate(
              auctionDescription,
              READ_DESCRIPTION_LIMIT,
              READ_DESCRIPTION_LIMIT_TRIM,
              this.toggleOverlay
            )}
            {this.getOverlayContent()}
          </div>
        </div>
      </>
    );
  }

  renderActiveAuctionFooter(data, bids, userData, bidToSet, minimumInput) {
    const { matchesBreakpoint: isDesktopOrLarger, wscoreData } = this.props;
    const sortedRankedBids = this.sortBidsPosition(bids);
    const yourPlace =
      sortedRankedBids.length && data.lastOwnBid
        ? sortedRankedBids.findIndex(
            item => item.amount === data.lastOwnBid.amount
          ) + 1
        : null;

    const lastOwnBid = data?.lastOwnBid?.amount ?? 0;
    const user = userData.user;

    const amountBid =
      this.state.editBid === true
        ? bidToSet
        : bidToSet > minimumInput
        ? bidToSet
        : minimumInput;

    const bidControls = data.status === 'InProgress' && (
      <div className="footer-bid-amount-controls-container">
        <div className="stepper-container">
          {!isDesktopOrLarger && this.renderChatModalButton()}
          <StepperInput
            disabled={data.status === 'Completed'}
            icon={WScoreIcon}
            value={
              this.state.editBid === true
                ? bidToSet
                : bidToSet > minimumInput
                ? bidToSet
                : minimumInput
            }
            step={data.bidIncrement}
            onIncrease={value => this.setState({ bidToSet: value })}
            onDecrease={value => {
              if (value >= minimumInput) {
                this.setState({ bidToSet: value });
              }
            }}
            onEdit={event => {
              const { value } = event.target;
              let result;
              if (typeof value === 'string') {
                result = value.replace(/[^0-9]/g, '');
                if (result === '') {
                  result = 0;
                } else {
                  result = result ? parseInt(result, 10) : this.state.bidToSet;
                }
              } else {
                result = value;
              }
              this.setState({ bidToSet: result });
            }}
            onFocus={_ => {
              if (this.state.editBid === false) {
                this.setState({
                  editBid: true,
                  bidToSet: minimumInput
                });
              }
            }}
          />
          {!isDesktopOrLarger && this.renderLeaderboardModalButton()}
        </div>
        {!!data.renewalCount && (
          <Hint
            thresholdGap={data.thresholdGap}
            incrementGap={data.incrementGap}
            renewalCount={data.renewalCount}
          />
        )}
        <Input
          className="footer-bid-confirmation"
          type="checkbox"
          label="Confirm my Auction bid"
          value={this.state.isModalConfirmationChecked}
          onChange={() =>
            this.setState({
              isModalConfirmationChecked: !this.state.isModalConfirmationChecked
            })
          }
        />
        {data.status === 'InProgress' && data.userInGroup && (
          <button
            className="btn button-color-green footer-bid-button"
            onClick={() =>
              !this.state.isModalConfirmationChecked
                ? this.handleAddBid({ amount: amountBid })
                : this.handleConfirmBid({
                    amount: amountBid,
                    min: minimumInput,
                    max: wscoreData.patronAssets.wscore,
                    bidIncrement: data.bidIncrement
                  })
            }
            type="button"
          >
            Bid
          </button>
        )}
      </div>
    );

    const completedMessage = data.status === 'Completed' && (
      <div className="footer-completed-auction-msg">
        <span>You cannot place a bid because this auction is over.</span>
        <Link
          className="btn button-color-blue auction-control-btn-back"
          to="/auctions"
          onClick={() => this.props.toggleFeedVisibility(false)}
        >
          Back to Auctions
        </Link>
      </div>
    );

    return (
      <div className="auction-right-panel-active-footer">
        <div className="footer-title-container">
          <div className="auction-leaderboard-position">
            {yourPlace ? yourPlace : '-'}
          </div>
          <div className="you">
            <ProfileIcon avatarData={user.avatarData} withBorder size={30} />
            <span className="header-display-name">{user.displayName}</span>
          </div>
          <div className="wscore-bidded">
            <img src={WScoreIcon} alt="" />
            <span> {lastOwnBid} </span>
          </div>
        </div>
        {data.userInGroup ? (
          bidControls
        ) : (
          <div className="auction-not-eligible">
            <div>You are not eligible to bid in this auction.</div>
            <div>Feel free to watch the auction to see who wins!</div>
          </div>
        )}
        {completedMessage}
      </div>
    );
  }

  renderUpcomingAuctionFooter() {
    const { matchesBreakpoint: isDesktopOrLarger } = this.props;

    return (
      <div className="auction-right-panel-upcoming-footer">
        <div className="wscore-info">
          <img src={WScoreIcon} alt="" />
          <span>
            Before this auction starts, play games and win more WScore!
          </span>
        </div>
        <div className="buttons-container">
          {!isDesktopOrLarger && this.renderChatModalButton()}
          <div className="games-button">
            <Link
              className="btn button-color-green back-to-games-button"
              to={{
                pathname: '/slots',
                state: { initiator: 'auctions-detail-go-to-games' }
              }}
            >
              Go to Games
            </Link>
          </div>
          {!isDesktopOrLarger && this.renderLeaderboardModalButton()}
        </div>
      </div>
    );
  }

  renderAuctionWinner(
    data,
    isAuctionCloseErrorModalOpen,
    hasAnimBidClosedFinished
  ) {
    return (
      <div className="auction-completed-left">
        <ResultModal
          show={isAuctionCloseErrorModalOpen}
          title={'Sorry!'}
          onClose={this.handleAuctionCloseErrorModalClose}
          closeBtnCaption={'Return'}
          closeBtnVariant={'green'}
        >
          <div className="auction-page-modal-msg">
            Sorry, the auction is closed.
          </div>
        </ResultModal>
        <div className="auction-page-anim-bid-close">
          <AlphaVideo
            width={300}
            src={AnimationBidClose}
            onVideoStop={this.handleAnimBidCloseFinish}
            mute
          />
        </div>
        <div
          className={`auction-completed-result ${
            hasAnimBidClosedFinished ? 'auction-completed-result-finished' : ''
          }`}
        >
          <div className="auction-completed-announcement">
            <div>Auction Complete!</div>
            {data.winner && <div>The winner is:</div>}
          </div>
          {data.winner && (
            <React.Fragment>
              {this.renderWinnerDisplayName(data.winner.user.displayName)}
              <div className="auction-completed-bid">
                <div className="auction-completed-bid-label">
                  With a bid of:
                </div>
                <div className="auction-completed-bid-value">
                  {formatNumber(data.winner.bid)}
                </div>
              </div>
            </React.Fragment>
          )}
        </div>
      </div>
    );
  }

  render() {
    const {
      auctionData,
      userData,
      wscoreData,
      matchesBreakpoint: isDesktopOrLarger
    } = this.props;
    const { loading, error, data } = auctionData;
    const { loading: userLoading, error: userError } = userData;
    const { loading: wscoreLoading, error: wscoreError } = wscoreData;
    const {
      bidAttempt,
      isErrorModalOpen,
      isMinBidErrorModalOpen,
      isAuctionCloseErrorModalOpen,
      isMaxBidErrorModalOpen,
      isConfirmBidModalOpen,
      hasAnimBidClosedFinished
    } = this.state;

    if (loading || userLoading || wscoreLoading) {
      return <LoadingIndicator overlay />;
    }

    if (error || userError || wscoreError) {
      return (
        <NotificationWindow
          message={'An error occurred during loading an auction.'}
          closeUrl="/auctions"
        />
      );
    }

    if (!data) {
      return (
        <NotificationWindow
          message={'Sorry, an error occurred or the auction does not exist.'}
          closeUrl="/auctions"
        />
      );
    }

    if (data.status === 'Deleted') {
      return (
        <NotificationWindow
          message={'Sorry, the auction has been removed.'}
          closeUrl="/auctions"
        />
      );
    }

    if (!this.hasSubscribed) {
      this.subscribe();
    }

    const bids = data.bids ? [...data.bids] : [];
    bids.sort((a, b) => b.amount - a.amount);

    const highBid = bids.length ? bids[0].amount : 0;
    const minimumInput = highBid
      ? highBid + data.bidIncrement
      : data.minimumBid;

    const bidToSet =
      this.state.bidToSet === null ? minimumInput : this.state.bidToSet;

    const auctionStatus = data.status;
    const startTime = data.startTime;
    const endTime = this.state.endTime || data.extendedEndTime || data.endTime;
    const auctionDescription = data.description;
    const prizes = data.prizes.prizes;

    return (
      <div className="page-container auction-page">
        <div
          className={`auction-page-right-panel auction-${kebabCase(
            auctionStatus
          )}`}
        >
          <div className="auction-right-panel-header">
            <div
              className={`auction-countdown ${
                auctionStatus === 'Upcoming'
                  ? 'upcoming-auction'
                  : 'active-auction'
              }`}
            >
              {this.renderHeaderCountdown(auctionStatus, endTime, startTime)}
            </div>
            <CloseButton url="/auctions" />
          </div>
          <div className="auction-right-panel-body-container">
            <div className="body-left-panel">
              {(auctionStatus === 'InProgress' ||
                auctionStatus === 'Upcoming') &&
                this.renderBodyAuctionDescription(
                  data,
                  prizes,
                  auctionDescription
                )}
              {auctionStatus === 'Completed' &&
                this.renderAuctionWinner(
                  data,
                  isAuctionCloseErrorModalOpen,
                  hasAnimBidClosedFinished
                )}
            </div>
            <div className="body-right-panel">
              {auctionStatus === 'Upcoming' && (
                <UpcomingAuctionBidInfo data={data} highBid={highBid} />
              )}
              {(auctionStatus === 'InProgress' ||
                auctionStatus === 'Completed') && (
                <InProgressAuctionBidInfo
                  data={data}
                  bids={bids}
                  endTime={endTime}
                  minimumInput={minimumInput}
                />
              )}
            </div>
          </div>
          {auctionStatus === 'Upcoming' && this.renderUpcomingAuctionFooter()}
          {(auctionStatus === 'InProgress' || auctionStatus === 'Completed') &&
            this.renderActiveAuctionFooter(
              data,
              bids,
              userData,
              bidToSet,
              minimumInput
            )}
          <div className="auction-page-center-left">
            {auctionStatus === 'InProgress' && (
              <React.Fragment>
                <ResultModal
                  show={isConfirmBidModalOpen}
                  title={'Confirm Bid'}
                  onClose={this.handleConfirmBidModalClose}
                  closeBtnCaption={'Cancel'}
                  closeBtnVariant={'blue'}
                  buttons={[
                    {
                      caption: 'Confirm',
                      onClick: () =>
                        this.handleConfirmBid({
                          amount: bidAttempt,
                          min: minimumInput,
                          max: wscoreData.patronAssets.wscore,
                          bidIncrement: data.bidIncrement
                        }),
                      variant: 'green'
                    }
                  ]}
                >
                  {data.lastOwnBid && highBid === data.lastOwnBid.amount ? (
                    <React.Fragment>
                      <div>
                        Are you sure you want to bid{' '}
                        <strong className="bid-wscore">
                          {bidAttempt} WScore
                        </strong>{' '}
                        again?
                      </div>
                    </React.Fragment>
                  ) : (
                    <div>
                      Are you sure you want to bid{' '}
                      <strong className="bid-wscore">
                        {bidAttempt} WScore
                      </strong>
                      ?
                    </div>
                  )}
                  <div>You will not be able to delete your bid.</div>
                </ResultModal>
                <ResultModal
                  show={isErrorModalOpen}
                  title={'Sorry!'}
                  onClose={this.handleErrorModalClose}
                  closeBtnCaption={'Return'}
                  closeBtnVariant={'green'}
                >
                  <div className="auction-page-modal-msg">
                    An error occurred while trying to place a bid. Please try
                    again.
                  </div>
                </ResultModal>
                <ResultModal
                  show={isMinBidErrorModalOpen}
                  title={'Sorry!'}
                  onClose={this.handleMinBidErrorModalClose}
                  closeBtnCaption={'Return'}
                  closeBtnVariant={'green'}
                >
                  <div className="auction-page-modal-msg">
                    The bid you tried to place is lower than the current top
                    bid. Please try again.
                  </div>
                </ResultModal>
                <ResultModal
                  show={isAuctionCloseErrorModalOpen}
                  title={'Sorry!'}
                  onClose={this.handleAuctionCloseErrorModalClose}
                  closeBtnCaption={'Return'}
                  closeBtnVariant={'green'}
                >
                  <div className="auction-page-modal-msg">
                    Sorry, the auction is closed.
                  </div>
                </ResultModal>
                <ResultModal
                  show={isMaxBidErrorModalOpen}
                  title={'Sorry!'}
                  onClose={this.handleMaxBidErrorModalClose}
                  closeBtnCaption={'Return'}
                  closeBtnVariant={'green'}
                >
                  You don&apos;t have enough WScore to place this bid. Please
                  try again.
                  <div className="auction-page-modal-msg"></div>
                </ResultModal>
              </React.Fragment>
            )}
          </div>
        </div>
        {isDesktopOrLarger && (
          <div className="auction-page-left">
            {this.renderLeaderBoard(data, userData, bids, endTime, prizes)}
            <GameChat
              room={`auc_${data.id}`}
              title="Auction Chat"
              participantData={data.bids}
            />
          </div>
        )}
      </div>
    );
  }
}

const HintIcon = ({ alt }) => (
  <A11ySvg component={HintIconSvg} className="svg-icon" title alt={alt} />
);

const Hint = ({ thresholdGap, incrementGap, renewalCount }) => (
  <div className="auction-time-hint">
    <IconButton
      icon={HintIcon}
      title
      alt="Extension info"
      tooltipMessage={
        <div>
          <div className="auction-time-hint-paragraph">
            The auction closing time will be extended by {incrementGap} seconds
            each time a new bid is placed during the last {thresholdGap} seconds
            of this auction.
          </div>
          <div className="auction-time-hint-paragraph">
            These extensions will occur a maximum of {renewalCount} times before
            the end of the auction.
          </div>
        </div>
      }
      tooltipClass="auction-time-hint-tooltip"
    />
  </div>
);

// class ExtendedBy extends Component {
//   renderDuration = () => {
//     const { duration } = this.props;
//     const days = duration.days();
//     const hours = duration.hours();
//     const minutes = duration.minutes();
//     const seconds = duration.seconds();

//     if (days) return days === 1 ? '1 Day' : `${days} Days`;
//     if (hours)
//       return `${zeroPad(hours)}:${zeroPad(minutes)}:${zeroPad(seconds)}`;
//     return (
//       <span className={minutes < 1 ? 'red-timer' : 'yellow-timer'}>
//         {zeroPad(hours)}:{zeroPad(minutes)}:{zeroPad(seconds)}
//       </span>
//     );
//   };

//   render() {
//     return <div className="auction-extended-by">+{this.renderDuration()}</div>;
//   }
// }

const ResultModal = ({
  show,
  title,
  onClose,
  closeBtnCaption,
  closeBtnVariant,
  closeBtnClassName,
  buttons,
  children
}) => (
  <ADialog
    className="auction-page-modal"
    show={show}
    title={title}
    onClose={onClose}
    closeIcon
    closeBtnCaption={closeBtnCaption}
    closeBtnVariant={closeBtnVariant}
    closeBtnClassName={closeBtnClassName}
    buttons={buttons}
  >
    <div className="auction-page-modal-content">{children}</div>
  </ADialog>
);

const AuctionPageConnected = compose(
  getUserData({
    name: 'userData',
    props: ({ userData }) => {
      return {
        userData,
        subscribeToLocationData: () => {
          return userData.subscribeToMore({
            document: SUBSCRIPTION_PREFERRED_LOCATION_CHANGED,
            updateQuery: (prev, { subscriptionData }) => {
              if (
                subscriptionData?.data?.newPreferredLocation &&
                subscriptionData.data.newPreferredLocation !==
                  prev.user.preferredLocation
              ) {
                return {
                  ...prev,
                  user: {
                    ...prev.user,
                    preferredLocation:
                      subscriptionData.data.newPreferredLocation
                  }
                };
              }
              return prev;
            }
          });
        }
      };
    }
  }),
  graphql(QUERY_GET_WSCORE, {
    name: 'wscoreData',
    options: props => ({
      fetchPolicy: 'network-only'
    }),
    props: ({ wscoreData, ownProps }) => {
      return {
        wscoreData,
        subscribeToWScoreData: () => {
          return wscoreData.subscribeToMore({
            document: SUBSCRIPTION_UPDATED_CURRENCY_ITEM_BALANCES,
            updateQuery: (prev, { subscriptionData: { data } }) => {
              const { wscoreBalance } = data;
              let result = cloneDeep(prev);

              if (!isNil(wscoreBalance)) {
                result.patronAssets.wscore = wscoreBalance;
              }

              return result;
            }
          });
        }
      };
    }
  }),
  graphql(QUERY_GET_AUCTION, {
    name: 'auctionData',
    options: props => {
      const { match } = props;
      const { id } = match.params;
      return {
        variables: {
          input: {
            auctionId: id
          }
        },
        fetchPolicy: 'network-only'
      };
    },
    props: ({ auctionData, ownProps }) => {
      const { match } = ownProps;
      const auctionId = parseInt(match.params.id, 10);

      return {
        auctionData: {
          ...auctionData,
          data: auctionData ? auctionData.auction : {}
        },
        subscribeToEvents: (userId, onSubscribeToEventsUpdate) => {
          return auctionData.subscribeToMore({
            document: SUBSCRIPTION_AUCTION_EVENT,
            updateQuery: (prev, { subscriptionData: { data } }) => {
              const { event, id, bids, totalBidsCount, extendedEndTime } = data;
              let result = cloneDeep(prev);

              if (event === 'start' && id === auctionId) {
                result.auction.status = 'InProgress';
              } else if (event === 'finalize' && id === auctionId) {
                result.auction.status = 'Completed';
                result.auction.winner = data.winner;
              } else if (event === 'extend' && id === auctionId) {
                result.auction.extendedEndTime = extendedEndTime;
              } else if (event === 'bid' && id === auctionId) {
                result.auction.bids = result.auction.bids
                  ? [...result.auction.bids, ...bids]
                  : [...bids];

                result.auction.bids.sort((a, b) => b.amount - a.amount);

                if (onSubscribeToEventsUpdate) {
                  onSubscribeToEventsUpdate(extendedEndTime, result.auction);
                }

                result.auction.bids = Array.from(
                  new Set(result.auction.bids.map(a => a.user.userId))
                ).map(useId => {
                  return result.auction.bids.find(a => a.user.userId === useId);
                });

                result.auction.totalBidsCount = totalBidsCount;
                const lastBid = bids.reduce((prev, current) =>
                  prev.amount > current.amount ? prev : current
                );
                if (userId === lastBid.user.userId) {
                  result.auction.lastOwnBid = {
                    __typename: 'AuctionBid',
                    bidTime: lastBid.bidTime,
                    amount: lastBid.amount
                  };
                }
              }

              return result;
            }
          });
        },
        subscribeToUpdates: () => {
          return auctionData.subscribeToMore({
            document: SUBSCRIPTION_AUCTION_UPDATE,
            updateQuery: (prev, { subscriptionData: { data } }) => {
              const { event, auctionId: id, ...rest } = data;
              if (id !== auctionId) {
                return null;
              }
              let result = cloneDeep(prev);

              if (event === 'updated') {
                mergeWith(result.auction, { id, ...rest }, (oldVal, newVal) => {
                  if (Array.isArray(newVal)) {
                    return newVal;
                  }
                });
              }

              if (event === 'deleted') {
                result.auction.status = 'Deleted';
              }

              return result;
            }
          });
        }
      };
    }
  }),
  graphql(MUTATION_ADD_BID, {
    props: ({ ownProps, mutate }) => ({
      createBid: bidData => {
        return mutate({
          variables: {
            input: { ...bidData }
          }
        });
      }
    }),
    withRef: true
  }),
  withMediaQuery(LG_BREAKPOINT_AND_UP)
)(AuctionPage);

export default TimeSyncWrapper(withRouter(AuctionPageConnected));
